import { FCX, Tooltip, useUuid } from "@datassential/platform-ui-lib";
import './RespondentsData.scss';
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import { useCallback, useState } from "react";
import { IFoodProfileConsumerData } from "@models/FoodProfileConsumer";
import { ArrayElement } from "@models";
import FoodBarChart from "@pages/Food/components/BarChart/BarChart";
import AnimatedNumber from "@pages/Food/components/AnimatedNumber";

export enum AnalyticsType {
    Affinity = 'Affinity',
    Experience = 'Experience',
}

export enum ValueType {
    Raw,
    Norm,
}

const FoodRespondentsData: FCX<{
    selectedAnalyticsType: AnalyticsType;
    data: IFoodProfileConsumerData['dataByDemoGroups'];
}> = ({
    selectedAnalyticsType,
    data,
}) => {
    const normTabTooltipId = useUuid();
    const [selectedValueType, setSelectedValueType] = useState<ValueType>(ValueType.Raw);
    const title = selectedAnalyticsType === AnalyticsType.Affinity
        ? 'LOVE OR LIKE IT'
        : 'KNOW IT';

    const getItemValue = useCallback(
        (item: ArrayElement<ArrayElement<IFoodProfileConsumerData['dataByDemoGroups']>['items']>) => {
            if (selectedValueType === ValueType.Raw && selectedAnalyticsType === AnalyticsType.Affinity) {
                return item.loveLikeItRaw;
            } else if (selectedValueType === ValueType.Norm && selectedAnalyticsType === AnalyticsType.Affinity) {
                return item.loveLikeItNorm;
            } else if (selectedValueType === ValueType.Raw && selectedAnalyticsType === AnalyticsType.Experience) {
                return item.knowItRaw;
            } else if (selectedValueType === ValueType.Norm && selectedAnalyticsType === AnalyticsType.Experience) {
                return item.knowItNorm;
            } else {
                return 0;
            }
        },
        [selectedValueType, selectedAnalyticsType],
    );

    return (
        <div className="FoodRespondentsData">
            <div className="FoodWidget__header">
                <div data-testid="textFoodConsumerRespondentsDataTitle">
                    {title}
                </div>
                <FoodTabs
                    data={[
                        { id: ValueType.Raw, title: 'Raw' },
                        { id: ValueType.Norm, title: (
                            <div data-tooltip-id={normTabTooltipId}>Norm</div>
                        ) },
                    ]}
                    activeTabId={selectedValueType}
                    onItemClick={(id) => setSelectedValueType(id as ValueType)}
                    testId="blockFoodConsumerRespondentsDataValueTypeTabs"
                />
                <Tooltip id={normTabTooltipId} place="top-end">
                    <div
                        style={{ fontSize: 12, lineHeight: '15px' }}
                        data-testid="textFoodConsumerRespondentsDataNormTabTooltip"
                    >
                        Norms versus all foods and beverage
                    </div>
                </Tooltip>
            </div>
            <div className="FoodRespondentsData__content">
                {data.map(({ groupName, items, }, index) => (
                    <div key={groupName} className="FoodRespondentsData__group">
                        {groupName && (
                            <div
                                className="FoodRespondentsData__group-title"
                                data-testid={`textFoodConsumerRespondentsDataGroupTitle-${index}`}
                            >
                                {groupName}
                            </div>
                        )}
                        <div className="FoodRespondentsData__group-items">
                            {items.map((item, itemIndex) => {
                                return (
                                    <div key={item.id} className="FoodRespondentsData__group-item">
                                        <div
                                            className="FoodRespondentsData__group-item-title"
                                            data-testid={`textFoodConsumerRespondentsDataGroupItemTitle-${index}-${itemIndex}`}
                                        >
                                            {item.name}
                                        </div>
                                        <FoodBarChart
                                            className="FoodRespondentsData__group-item-bar FoodSectionConsumer__blur"
                                            colorStart="#EB0089"
                                            colorStop="#FF5AB9"
                                            valuePct={getItemValue(item)}
                                            hasInlay
                                        />
                                        <AnimatedNumber
                                            testId={`textFoodConsumerRespondentsDataGroupItemValue-${index}-${itemIndex}`}
                                            value={getItemValue(item)}
                                            valueFormatter={(v) => `${Math.round(v)}${selectedValueType === ValueType.Raw ? '%' : ''}`}
                                            className="FoodRespondentsData__group-item-value FoodSectionConsumer__blur"
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default FoodRespondentsData;